import React, { useState, useEffect } from 'react';
import { CustomTable, Search, Pagination, Loader, ConfirmationPopup, SuccessModal, Tooltip } from '@ohif/ui';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import ConsultationForm from './ConsultationForm';
import getConsultationCharges from '@custom/rtk-apis/billing/consultation/getConsultationCharges';
import deleteConsultationCharges from '@custom/rtk-apis/billing/consultation/deleteConsultationCharges';
import addConsultationCharges from '@custom/rtk-apis/billing/consultation/addConsultationCharges';
import getModalityStudy from '@custom/rtk-apis/modalityStudyRegion/getModalityStudy';
import getRadiologistList from '@custom/rtk-apis/billing/consultation/getRadiologistList';
import { clearConsultationCharges } from '@custom/store/slices/billing/consultation/getConsultationCharges';
import { clearAddConsultationCharges } from '@custom/store/slices/billing/consultation/addConsultationCharges';
import { clearDeleteConsultationCharges } from '@custom/store/slices/billing/consultation/deleteConsultationCharges';
import editConsultationCharges from '@custom/rtk-apis/billing/consultation/editConsultationCharges';
import { clearEditConsultationCharges } from '@custom/store/slices/billing/consultation/editConsultationCharges';
import getBillingGeneralSettings from '@custom/rtk-apis/billing/getGeneralSettings';
import { capitalizeWords, getCurrencySymbol } from '@custom/Utils/utils';
import { useNavigate } from 'react-router-dom';
import { servicesManager } from '../../app/src/App';

interface ConsultationProps {
  activeTab: string;
  setActiveTab?: (tab: string) => void;
}

const ConsultationCharges = (props: ConsultationProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [tableData, setTableData] = useState([]);
  const [pgNumber, setPgNumber] = useState(1);
  const [pgLimit, setPgLimit] = useState(30);
  const [totalPg, setTotalPg] = useState(10);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [editConsultation, setEditConsultation] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [success, setSuccess] = useState(false);
  const [msg, setMsg] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [edit, setEdit] = useState(false);
  const [modalities, setModalities] = useState([]);
  const [radiologistNames, setRadiologistNames] = useState([]);
  const [currency, setCurrency] = useState("");
  const [existingRadiologists, setExistingRadiologists] = useState([]);
  const { uiNotificationService } = servicesManager.services;

  const consultationData = useSelector((state: RootState) => state.getConsultationChargesResponse);
  const addConsultationChargesData = useSelector((state: RootState) => state.addConsultationChargesResponse);
  const deleteConsultationData = useSelector((state: RootState) => state.deleteConsultationChargesResponse);
  const editConsultationData = useSelector((state: RootState) => state.editConsultationChargesResponse);
  const modalityAndStudyRegion = useSelector((state: RootState) => state.getModalityStudySliceResponse);
  const radiologistList = useSelector((state: RootState) => state.getRadiologistListResponse);
  const billingGeneralSettingsData = useSelector((state: RootState) => state.getBillingGeneralSettingsSliceResponse);

  useEffect(() => {
    dispatch(getBillingGeneralSettings());
  }, [dispatch])

  useEffect(() => {
    if (billingGeneralSettingsData?.result?.currency) {
      setCurrency(billingGeneralSettingsData?.result?.currency);
    }
  }, [billingGeneralSettingsData]);

  const columns = [
    { key: 'radiologistName', title: 'Radiologist Name' },
    { key: 'modality', title: 'Modality' },
    { key: 'consultationChargesFormatted', title: 'Consultation Charges', sort: true },
    { key: 'actions', title: 'Action' },
  ];

  const defaultInitialValues = {
    radiologistId: '',
    radiologistName: '',
    consultationCharges: [
      {
        modality: '',
        charge: ''
      }
    ]
  };

  const [initialValues, setInitialValues] = useState(defaultInitialValues);

  const submitForm = async (values) => {
    const payload = {
      hospitalId: sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId'),
      radiologistId: values?.radiologistId,
      radiologistName: capitalizeWords(values?.radiologistName),
      consultationCharges: values?.consultationCharges,
    };
    if (edit) {
      dispatch(editConsultationCharges(payload));
    } else {
      dispatch(addConsultationCharges(payload));
    }
  };

  const handleAddCharges = () => {
    if (billingGeneralSettingsData?.msg === "General settings not found for this hospital.") {
      props.setActiveTab?.('general');
      uiNotificationService.show({
        title: 'Billing Settings',
        message: 'Fill the general settings to create appointment.',
        type: 'error',
      });
      navigate('/billing');
    } else {
      dispatch(getModalityStudy());
      dispatch(getRadiologistList());
      setEditConsultation(null);
      setEdit(false);
      setInitialValues(defaultInitialValues);
      setShowConsultationForm(true);
    }
  };

  const handleDelete = async () => {
    if (selectedRow) {
      setIsDeleting(true);
      const payload = {
        hospitalId: sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId'),
        radiologistId: selectedRow?.radiologistId,
      };
      dispatch(deleteConsultationCharges(payload));
    }
  };

  const handleCancel = () => {
    setIsPopupOpen(false);
  };

  const actions = [
    {
      id: 'editConsultation',
      label: 'Edit',
      iconName: 'edit',
      onClick: (item) => {
        setEdit(true);
        setEditConsultation(item);
        setInitialValues({
          radiologistId: item.radiologistId || '',
          radiologistName: item.radiologistName || '',
          consultationCharges: item.consultationCharges || '',
        });
        dispatch(getModalityStudy());
        dispatch(getRadiologistList());
        setShowConsultationForm(true);
      },
    },
    {
      id: 'deleteConsultation',
      label: 'Delete',
      iconName: 'delete',
      onClick: (item) => {
        setSelectedRow(item);
        setIsPopupOpen(true);
      },
    },
  ];

  const searchClick = (searchText) => {
    setSearchInputValue(searchText);
    setSearchQuery(searchText);
    setPgNumber(1);
    dispatch(getConsultationCharges({ search: searchText, page: 1, limit: pgLimit }));
  };

  useEffect(() => {
    if (deleteConsultationData.status === 'success') {
      setMsg('Consultation deleted successfully');
      setSuccess(true);
      setIsDeleting(false);
      setIsPopupOpen(false);
      setTimeout(() => {
        dispatch(getConsultationCharges({ search: searchQuery, page: pgNumber, limit: pgLimit }));
      }, 500);
    }
    return () => {
      dispatch(clearDeleteConsultationCharges());
    };
  }, [deleteConsultationData]);

  useEffect(() => {
    dispatch(getConsultationCharges({ search: searchQuery, page: pgNumber, limit: pgLimit }));
  }, [dispatch, searchQuery, pgNumber, pgLimit]);

  useEffect(() => {
    if (consultationData.status === 'success' && consultationData?.result?.data?.charges) {
      const symbol = getCurrencySymbol(currency);

      const formattedCharges = consultationData?.result?.data?.charges.map(item => {
        const modalities = item?.consultationCharges?.map(c => c.modality);
        const charges = item?.consultationCharges?.map(c => `${symbol}${c.charge}`);

        const formattedModality = modalities.length > 2
          ? `${modalities[0]}, ${modalities[1]}, ...`
          : modalities.join(', ');

        const formattedCharge = charges.length > 2
          ? `${charges[0]}, ${charges[1]}, ...`
          : charges.join(', ');

        return {
          ...item,
          radiologistName: item.radiologistName,
          modality: (
            <Tooltip content={modalities.join(', ')} position="bottom-left">
              {formattedModality}
            </Tooltip>
          ),
          consultationChargesFormatted: (
            <Tooltip content={charges.join(', ')} position="bottom-left">
              {formattedCharge}
            </Tooltip>
          ),
        };
      });
      setTotalPg(consultationData?.result?.total);
      setTableData(formattedCharges);
    }

    return () => {
      dispatch(clearConsultationCharges());
    };
  }, [consultationData?.result?.data, consultationData.status, consultationData.error, currency]);

  useEffect(() => {
    if (modalityAndStudyRegion.status === 'SUCCESS' && modalityAndStudyRegion.result) {
      setModalities(modalityAndStudyRegion.result.modalities);
    }
  }, [modalityAndStudyRegion.result, modalityAndStudyRegion.status, modalityAndStudyRegion.error]);

  useEffect(() => {
    if (radiologistList?.status === 'SUCCESS') {
      setRadiologistNames(radiologistList?.result);
    }
  }, [radiologistList.status, consultationData?.result?.data, consultationData.status]);

  useEffect(() => {
    const existingRadiologistIds = tableData?.map(
      item => item?.radiologistId
    );

    setExistingRadiologists(existingRadiologistIds);

  }, [tableData]);

  useEffect(() => {
    if (addConsultationChargesData?.status === 'SUCCESS') {
      setSuccess(true);
      setMsg('Consultation charges added successfully.');
    }
    return () => {
      setTimeout(() => {
        setShowConsultationForm(false);
        dispatch(getConsultationCharges({ search: searchQuery, page: pgNumber, limit: pgLimit }));
        dispatch(clearAddConsultationCharges());
      }, 500);
    };
  }, [addConsultationChargesData.status]);

  useEffect(() => {
    if (editConsultationData?.status === 'success') {
      setSuccess(true);
      setMsg('Consultation charges updated successfully.');
      setTimeout(() => {
        setShowConsultationForm(false);
        dispatch(getConsultationCharges({ search: searchQuery, page: pgNumber, limit: pgLimit }));
        dispatch(clearEditConsultationCharges());
      }, 500);
    }
  }, [editConsultationData?.status]);

  return (
    <>
      <div className="page__body">
        <div className="tableWrap">
          {!showConsultationForm ? (
            <>
              <div className="tableWrap__head">
                <div className="formRow formRow__2 m_0 w_100 formRow--flexEnd">
                  <div className="formCol">
                    <Search
                      text="Search"
                      onSearchClick={searchClick}
                      value={searchInputValue}
                      setValue={setSearchInputValue}
                    />
                  </div>
                </div>
                {props.activeTab === 'consultationCharges' && (
                  <div className="btn__group">
                    <button className="btn btn__white" onClick={handleAddCharges}>
                      <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M7.63509 1.16699V12.8337M1.80176 7.00033H13.4684"
                          stroke="#150823"
                          strokeWidth="1.67"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Add Charges
                    </button>
                  </div>
                )}
              </div>
              <div className="tableContainer">
                <CustomTable
                  columns={columns}
                  data={tableData}
                  actions={actions}
                  searchQuery={searchQuery}
                  from='consultation'
                />
              </div>
            </>
          ) : (
            <ConsultationForm
              editConsultation={editConsultation}
              submitForm={submitForm}
              edit={edit}
              setEdit={setEdit}
              modalities={modalities}
              radiologistNames={radiologistNames}
              initialValues={initialValues}
              setShowConsultationForm={setShowConsultationForm}
              isSubmitting={false}
              existingRadiologists={existingRadiologists}
            />
          )}
        </div>
      </div>
      <SuccessModal
        isSuccessModalOpen={success}
        title={msg}
        handleCloseModal={() => setSuccess(false)}
      />
      <ConfirmationPopup
        isOpen={isPopupOpen}
        message={`Are you sure you want to delete the ${selectedRow?.radiologistName}?`}
        onConfirm={handleDelete}
        onCancel={handleCancel}
        title={''}
      />
      <Loader isLoaderOpen={addConsultationChargesData.loading || consultationData.loading || editConsultationData.loading || deleteConsultationData.loading} />
      {tableData.length > 0 && !showConsultationForm && (
        <Pagination onChangePage={setPgNumber} onChangePerPage={setPgLimit} totalPage={totalPg} currentPage={pgNumber} perPage={pgLimit} />
      )}
    </>
  );
};

export default ConsultationCharges;
